import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import OnboardingService from '@/services/onboarding.service'

export const useOnboardingStore = defineStore('onboarding', () => {
    const steps = ref([])
    const dismissed = ref(false)
    const loaded = ref(false)
    const _accountUlid = ref(null)

    const checklistSteps = computed(() =>
        steps.value.filter((s) => s.key !== 'survey_completed')
    )
    const completedCount = computed(
        () => checklistSteps.value.filter((s) => s.completed).length
    )
    const total = computed(() => checklistSteps.value.length)
    const surveyCompleted = computed(() => {
        const step = steps.value.find((s) => s.key === 'survey_completed')
        // Treat absent milestone as done — survey only shows when the backend
        // explicitly returns the step with completed: false.
        if (!step) return true
        return step.completed === true
    })
    const percentComplete = computed(() =>
        total.value === 0
            ? 0
            : Math.round((completedCount.value / total.value) * 100)
    )

    function _loadDismissed(ulid) {
        dismissed.value =
            localStorage.getItem(`onboarding-dismissed-${ulid}`) === 'true'
    }

    function dismiss() {
        dismissed.value = true
        if (_accountUlid.value) {
            localStorage.setItem(
                `onboarding-dismissed-${_accountUlid.value}`,
                'true'
            )
        }
    }

    function _applyResponse(response) {
        const data = response?.data
        steps.value = Array.isArray(data) ? data : []
    }

    async function fetch(ulid) {
        _accountUlid.value = ulid
        _loadDismissed(ulid)
        try {
            const response = await OnboardingService.status(ulid)
            _applyResponse(response)
        } catch {
            // silently fail — checklist is non-critical
        } finally {
            loaded.value = true
        }
    }

    async function markComplete(eventKey) {
        if (!_accountUlid.value) return
        // optimistic update
        const step = steps.value.find((s) => s.key === eventKey)
        if (step) step.completed = true
        try {
            const response = await OnboardingService.markComplete(
                _accountUlid.value,
                eventKey
            )
            _applyResponse(response)
        } catch {
            // revert optimistic update on failure
            if (step) step.completed = false
        }
    }

    function open() {
        dismissed.value = false
    }

    return {
        steps,
        checklistSteps,
        dismissed,
        loaded,
        completedCount,
        total,
        percentComplete,
        surveyCompleted,
        fetch,
        dismiss,
        open,
        markComplete,
    }
})
